import {
  getProjectsKey,
  ProjectModel,
  ProjectMutationInput,
  useCancelProject,
} from 'api-hooks/projects';
import { SaleLiteModel } from 'api-hooks/sale/common';
import { Option } from 'baseui/select';
import { SaleTransactionTypeEnum } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState, Input } from 'components/elements';
import Text from 'components/elements/text';
import {
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
} from 'components/styles';
import FormHeader from 'components/widgets/form-header';
import useDialog from 'hooks/use-dialog';
import useNavigation from 'hooks/use-navigation';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import TitleStatus from 'legacy-modules/common/title-status';
import CustomerMultiInput from 'legacy-modules/master/customer/components/customer-multi-input';
import GuestMultiInput from 'legacy-modules/master/guest/components/guest-multi-input';
import SalesMultiInput from 'legacy-modules/sales/component/sales-multi-input';
import { FormDivider } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ActionComponent from './action-component';
import { ProjectFormSchema, ProjectFormType } from './form-type';
import ItineraryTable from './itinerary-table';
import SalesTable from './sales-table';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  project?: ProjectModel;
  onSubmit: (
    input: ProjectMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ProjectModel | undefined>;
}

function transformer(item: SaleLiteModel): Option {
  return {
    id: item.id,
    label: item.invoiceNumber,
    extra: item,
  };
}

export default function ProjectForm(props: Props) {
  const { t } = useTranslation();
  const { project } = props;
  const { asPath } = useRouter();
  const dialog = useDialog();
  const { close } = useNavigation();
  const { mutateAsync: cancelMutate, isLoading: cancelLoading } =
    useCancelProject();

  const defaultValues = useGetDefaultValues(project);

  const resolver = useYupValidationResolver(ProjectFormSchema());

  const methods = useForm<ProjectFormType>({
    resolver,
    defaultValues,
    mode: 'onChange',
  });

  const onSubmit = React.useCallback(
    async (values: ProjectFormType) => {
      try {
        const projectCustomers: ProjectMutationInput['projectCustomers'] =
          values.projectCustomers.map((pc, index) => {
            return {
              customerId: pc.id,
              id: project?.projectCustomers?.[index]?.id,
            };
          });

        const projectGuests: ProjectMutationInput['projectGuests'] =
          values.projectGuests.map((pg, index) => {
            return {
              guestId: pg.id,
              id: project?.projectGuests?.[index]?.id,
            };
          });

        const sales: ProjectMutationInput['sales'] = values.sales.map(
          (sale) => {
            return {
              refId: sale.id,
            };
          },
        );
        await props.onSubmit(
          { ...values, projectCustomers, projectGuests, sales },
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, project, props],
  );

  const onCancel = React.useCallback(async () => {
    dialog.showConfirmation({
      message: t('common:confirmation_void_text'),
      title: t('common:confirmation'),
      onPositiveAction: async (dismiss) => {
        try {
          dismiss();

          await cancelMutate({
            projectId: project!.id!,
          });
          queryClient.refetchQueries([getProjectsKey()[0]]);

          close();
          notification.success({ message: t('common:successfully_voided') });
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (dismiss) => {
        dismiss();
      },
    });
  }, [project, close, cancelMutate, dialog, t]);

  const salesData = React.useCallback(() => {
    const _salesData: {
      tourInvoices: SaleLiteModel[];
      hotelInvoices: SaleLiteModel[];
      flightInvoices: SaleLiteModel[];
      documentInvoices: SaleLiteModel[];
      activityInvoices: SaleLiteModel[];
      cruiseInvoices: SaleLiteModel[];
      productInvoices: SaleLiteModel[];
      options: Option[];
    } = {
      tourInvoices: [],
      hotelInvoices: [],
      flightInvoices: [],
      documentInvoices: [],
      activityInvoices: [],
      cruiseInvoices: [],
      productInvoices: [],
      options: [],
    };

    project?.sales?.map((sale) => {
      _salesData.options.push(transformer(sale));
      switch (sale.transactionType) {
        case SaleTransactionTypeEnum.Tour:
          _salesData.tourInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Hotel:
          _salesData.hotelInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Flight:
          _salesData.flightInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Document:
          _salesData.documentInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Activity:
          _salesData.activityInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Cruise:
          _salesData.cruiseInvoices.push(sale);
          break;
        case SaleTransactionTypeEnum.Product:
          _salesData.productInvoices.push(sale);
          break;
        default:
          break;
      }
    });

    return _salesData;
  }, [project?.sales]);

  const { revertDraftButton } = useRevertDraftAction('projects', project);

  const actionComponent = project && (
    <div style={{ display: 'flex', flexDirection: 'row', gap: 16 }}>
      {revertDraftButton}
      <ActionComponent
        project={project}
        onCancel={onCancel}
        cancelLoading={cancelLoading}
        options={salesData().options}
      />
    </div>
  );
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!project}
      customViewName={
        project ? `${t('common:project')} - ${project.code}` : undefined
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={project}
            noEdit={!editable}
            noDelete
            title={
              project ? (
                <TitleStatus
                  status={project.status}
                  title={`${t('common:project')} - ${project.code}`}
                  type="sale-default"
                />
              ) : (
                t('common:add_extra', { extra: t('common:project') })
              )
            }
            onRenderComponent={editable ? undefined : <>{actionComponent}</>}
          />
        )}
      </FormState>
      <div>
        <SectionContainer>
          {project ? (
            <HalfContainer>
              <Input
                type="text"
                name="code"
                label={t('common:code')}
                placeholder={t('common:code')}
                disabled
              />
            </HalfContainer>
          ) : (
            <></>
          )}

          <HalfContainer>
            <Input
              type="text"
              name="name"
              label={t('common:project_name')}
              placeholder={t('common:project_name')}
              required
            />
          </HalfContainer>
        </SectionContainer>
        <FullContainer>
          <Input
            type="text-area"
            name="description"
            label={t('common:description')}
            placeholder={t('common:description')}
          />
        </FullContainer>
        <FullContainer>
          <Text variant="HeadingXSmall">{t('common:customer_guest')}</Text>
          <SeparatorVertical $margin={16} />
          <CustomerMultiInput name="projectCustomers" required />
          <SeparatorVertical $margin={16} />
          <GuestMultiInput name="projectGuests" type="guest" required />
          <SeparatorVertical $margin={16} />
          <SalesMultiInput
            name="sales"
            disabled={!!project}
            hasProject={false}
          />
        </FullContainer>
        <SeparatorVertical $margin={32} />
        <Text variant="HeadingXSmall">{t('common:itinerary')}</Text>
        <SeparatorVertical $margin={24} />
        <ItineraryTable project={project} />
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:tour_invoices')}</Text>
            <Separator gap={16} />
            {salesData().tourInvoices.length ? (
              <SalesTable
                data={salesData().tourInvoices}
                option={salesData().options}
              />
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:flight_invoices')}</Text>
            <Separator gap={16} />
            {salesData().flightInvoices.length ? (
              <SalesTable
                data={salesData().flightInvoices}
                option={salesData().options}
              />
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:hotel_invoices')}</Text>
            <Separator gap={16} />
            {salesData().hotelInvoices.length ? (
              <SalesTable
                data={salesData().hotelInvoices}
                option={salesData().options}
              />
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:cruise_invoices')}</Text>
            <Separator gap={16} />
            {salesData().cruiseInvoices.length ? (
              <SalesTable
                data={salesData().cruiseInvoices}
                option={salesData().options}
              />
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">
              {t('common:travel_document_invoices')}
            </Text>
            <Separator gap={16} />
            {salesData().documentInvoices.length ? (
              <SalesTable
                data={salesData().documentInvoices}
                option={salesData().options}
                project={project}
              />
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:other_invoices')}</Text>
            <Separator gap={16} />
            {salesData().activityInvoices.length ? (
              <>
                <SalesTable
                  data={salesData().activityInvoices}
                  option={salesData().options}
                />
              </>
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
        <Separator gap={32} />
        {project && (
          <>
            <Text variant="HeadingXSmall">{t('common:product_invoices')}</Text>
            <Separator gap={16} />
            {salesData().productInvoices.length ? (
              <>
                <SalesTable
                  data={salesData().productInvoices}
                  option={salesData().options}
                />
              </>
            ) : (
              <Text variant="LabelSmall">
                {t('common:you_have_no_data_at_this_moment')}
              </Text>
            )}
          </>
        )}
      </div>
      <FormDivider />
    </Form>
  );
}
